import type { ApiClient } from '@carbonwebmail/api-client'
import { UserProfile } from '@clerk/react'
import { Alert } from '../../ui/Alert.js'
import { Async } from '../../ui/Async.js'
import { Field } from '../../ui/Field.js'
import { useProfile } from './useProfile.js'

/**
 * Your own account, as distinct from the organisation you belong to.
 *
 * **The name is the only thing this product owns.** The address, the password
 * and the second factor belong to the identity provider, and its own screen is
 * embedded below rather than reimplemented — a password form here would be a
 * second place that believes it knows how signing in works, which is the
 * arrangement ADR 0004 exists to end.
 *
 * The address is shown beside the name as well, and shown rather than edited.
 * Moving an account to a new address means proving control of it, which is a
 * flow with a code in it rather than a field in a form — and that flow is the
 * provider's.
 */
export function ProfilePage({ client }: { readonly client: ApiClient }) {
  const profile = useProfile({ client })
  const { state, name, nameSaved, nameProblem, savingName } = profile

  return (
    <>
      <header className="mb-6">
        <p className="mt-1 text-[13px] text-t2">
          Your account, not the organisation you belong to.
        </p>
      </header>

      <Async
        state={state}
        skeleton={<p className="text-[13px] text-t3">Loading…</p>}
        onRetry={() => window.location.reload()}
      >
        {(loaded) => (
          <div className="max-w-2xl">
            {nameProblem === undefined ? null : <Alert>{nameProblem}</Alert>}
            {nameSaved ? <Alert tone="ok">Saved.</Alert> : null}

            <form
              className="rounded-card border border-line bg-surface p-5"
              onSubmit={(event) => {
                event.preventDefault()
                void profile.saveName()
              }}
            >
              <h2 className="mb-4 font-bold text-[15px] text-t1">Details</h2>

              <Field
                id="profile-name"
                label="Name"
                type="text"
                autoComplete="name"
                value={name ?? ''}
                disabled={savingName || name === undefined}
                optional
                hint="What the product calls you. Left empty, it uses your email address."
                onChange={profile.changeName}
              />

              {/* Read-only, and said plainly rather than left as a disabled box
                  somebody prods at wondering why it will not take. */}
              <div className="mt-4">
                <p className="mb-1.5 font-semibold text-[12px] text-t2">Email address</p>
                <p className="text-[14px] text-t1">{loaded.email}</p>
                <p className="mt-1.5 text-[12px] text-t3">
                  Changing this means proving you can receive mail at the new address, which is done
                  below rather than here.
                </p>
              </div>

              <button
                type="submit"
                disabled={savingName || name === undefined}
                className="mt-6 rounded-pill bg-brand px-5 py-2.5 font-bold text-[14px] text-brand-contrast disabled:cursor-not-allowed disabled:opacity-60"
              >
                {savingName ? 'Saving…' : 'Save'}
              </button>
            </form>

            {/*
              Sign-in, embedded rather than linked away to.

              Password, second factor, connected accounts and email addresses
              all live here — the provider's screen, inside this product's
              layout, so somebody changing their password does not lose where
              they were.
            */}
            <section className="mt-6">
              <h2 className="mb-2 font-bold text-[15px] text-t1">Sign-in and security</h2>
              <p className="mb-4 text-[13px] text-t2">
                Your password, two-step verification and the addresses you can sign in with.
              </p>
              <UserProfile
                routing="hash"
                appearance={{ elements: { rootBox: 'w-full', cardBox: 'w-full shadow-none' } }}
              />
            </section>
          </div>
        )}
      </Async>
    </>
  )
}
